import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"
import { AppConfiguration } from "config"
import {
  buildCampaignAnalysisCacheKey,
  getCurrentBrandFromLocalStorage,
  responseErrorChecker,
} from "helpers/sf_utils"
import { sf_fetch } from "helpers/sf_fetch"
import {
  genericGetRequest_2,
  genericPutRequest,
  genericPostRequest,
} from "../../helpers/sf_backend_helper"
import {
  GET_ALL_PROSPECTS,
  UPDATE_CPA_DISPOSITION,
  BULK_EDIT_CPA_DISPOSITION,
  GET_CPA_BULK_EDIT_STATUS,
  PREVIEW_CPA_BULK_EDIT,
} from "./actionTypes"
import {
  getAllProspectsSuccess,
  getAllProspectsError,
  updateCpaDispositionSuccess,
  updateCpaDispositionError,
  bulkEditCpaDispositionSuccess,
  bulkEditCpaDispositionError,
  getCpaBulkEditStatusSuccess,
  getCpaBulkEditStatusError,
  previewBulkEditCpaDispositionSuccess,
  previewBulkEditCpaDispositionError,
} from "./actions"

const CPA_BULK_EDIT_TARGET_KEY = "cpa_disposition"

const normalizeBulkEditJob = job => {
  if (!job) return null

  // cancellation_requested still holds the account lock until the worker
  // acknowledges it, so the page must stay locked for it too.
  const status = ["queued", "in_progress", "cancellation_requested"].includes(job.status)
    ? "processing"
    : job.status
  return { ...job, status }
}

const isActiveBulkEditStatus = status => ["queued", "in_progress", "cancellation_requested", "processing"].includes(status)

const parseBulkEditFilters = filters => {
  if (!filters) return {}
  if (typeof filters !== "string") return filters

  try {
    return JSON.parse(filters)
  } catch (_error) {
    return {}
  }
}

const buildBulkEditPayload = payload => ({
  target_key: CPA_BULK_EDIT_TARGET_KEY,
  selection: payload?.select_all
    ? { mode: "filters", filters: parseBulkEditFilters(payload?.filters) }
    : { mode: "ids", ids: Array.isArray(payload?.id) ? payload.id : [payload?.id].filter(Boolean) },
  changes: {
    disposition: payload?.disposition,
    reason: payload?.reason,
  },
})

function* onGetAllProspects(action) {
  try {
    const user = JSON.parse(localStorage.getItem("authUser")).user
    const queryParams = { ...(action?.payload?.queryParams || {}) }
    const pageNumber = Math.max(1, Number(queryParams.page_number) || 1)
    queryParams.page_number = pageNumber
    const cacheKey = buildCampaignAnalysisCacheKey(queryParams)
    const bypassCache = Boolean(action?.payload?.bypassCache)

    if (!bypassCache) {
      const cachedResponse = yield select(
        state => state?.AllProspects?.page_cache?.[cacheKey]?.[pageNumber]
      )
      if (cachedResponse) {
        yield put(
          getAllProspectsSuccess(cachedResponse, {
            appendResults: action?.payload?.appendResults,
            cacheKey,
            pageNumber,
            fromCache: true,
          })
        )
        return
      }
    }

    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/fetch_all_prospects`,
      queryParams,
      {
        Authorization: user.token,
        "account-id": user.account_id,
        "brand-id": getCurrentBrandFromLocalStorage()?.id,
      }
    )
    yield put(
      getAllProspectsSuccess(response, {
        appendResults: action?.payload?.appendResults,
        cacheKey,
        pageNumber,
        fromCache: false,
        invalidateCache: bypassCache,
      })
    )
  } catch (error) {
    yield put(getAllProspectsError(error))
  }
}

function* onUpdateCpaDisposition(action) {
  try {
    const user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPutRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/update_prospect_disposition`,
      {
        body: JSON.stringify({ ...action.payload }),
        headers: {
          Authorization: user.token,
          "account-id": user.account_id,
          "Content-Type": "application/json",
        },
      }
    )
    yield put(updateCpaDispositionSuccess(response))
  } catch (error) {
    yield put(updateCpaDispositionError(error))
  }
}

function* onBulkEditCpaDisposition(action) {
  try {
    const payload = buildBulkEditPayload(action.payload)
    if (payload.selection.mode === "ids" && payload.selection.ids.length === 0) {
      yield put(bulkEditCpaDispositionError(new Error("No targets specified for bulk edit")))
      return
    }
    const user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/bulk_edits`,
      {
        body: JSON.stringify(payload),
        headers: {
          Authorization: user.token,
          "account-id": user.account_id,
          "brand-id": getCurrentBrandFromLocalStorage()?.id,
          "Content-Type": "application/json",
        },
      }
    )
    yield put(bulkEditCpaDispositionSuccess(normalizeBulkEditJob(response)))
  } catch (error) {
    yield put(bulkEditCpaDispositionError(error))
  }
}

// Unlike genericPostRequest, /bulk_edits/preview renders its payload
// (count/target_key/lock_held/changes) at the top level rather than under a
// `data` key -- it matches the GET-only bulk_edits endpoints (show,
// lock_status) here, not the POST /bulk_edits `create` action. Using
// genericPostRequest's `.data` unwrap against this response would always
// resolve to undefined, so this posts without it.
const postRequestRaw = async (url, init) => {
  const response = await sf_fetch(url, { method: "POST", ...init })
  const responseText = await responseErrorChecker(response)
  return JSON.parse(responseText)
}

// Runs the same selection payload that onBulkEditCpaDisposition would submit
// against POST /bulk_edits/preview, so the confirmation modal can show how
// many records are actually affected (and whether another bulk edit already
// holds the lock) before the user commits to anything.
function* onPreviewBulkEditCpaDisposition(action) {
  try {
    const payload = buildBulkEditPayload(action.payload)
    if (payload.selection.mode === "ids" && payload.selection.ids.length === 0) {
      yield put(previewBulkEditCpaDispositionError(new Error("No targets specified for bulk edit")))
      return
    }
    const user = JSON.parse(localStorage.getItem("authUser")).user
    const response = yield call(
      postRequestRaw,
      `${AppConfiguration.BACKEND_URL}/api/v1/bulk_edits/preview`,
      {
        body: JSON.stringify(payload),
        headers: {
          Authorization: user.token,
          "account-id": user.account_id,
          "brand-id": getCurrentBrandFromLocalStorage()?.id,
          "Content-Type": "application/json",
        },
      }
    )
    yield put(previewBulkEditCpaDispositionSuccess(response))
  } catch (error) {
    yield put(previewBulkEditCpaDispositionError(error))
  }
}

function* onGetCpaBulkEditStatus() {
  try {
    const user = JSON.parse(localStorage.getItem("authUser")).user
    const headers = {
      Authorization: user.token,
      "account-id": user.account_id,
      "brand-id": getCurrentBrandFromLocalStorage()?.id,
    }
    const currentJob = yield select(state => state?.AllProspects?.bulk_edit_job)
    let response = null

    if (currentJob?.id && isActiveBulkEditStatus(currentJob.status)) {
      response = yield call(
        genericGetRequest_2,
        `${AppConfiguration.BACKEND_URL}/api/v1/bulk_edits/${currentJob.id}`,
        {},
        headers
      )
    } else {
      const lockStatus = yield call(
        genericGetRequest_2,
        `${AppConfiguration.BACKEND_URL}/api/v1/bulk_edits/lock_status`,
        { target_key: CPA_BULK_EDIT_TARGET_KEY },
        headers
      )
      if (lockStatus?.active_request_id) {
        try {
          response = yield call(
            genericGetRequest_2,
            `${AppConfiguration.BACKEND_URL}/api/v1/bulk_edits/${lockStatus.active_request_id}`,
            {},
            headers
          )
        } catch (_error) {
          // The account is locked even if the job details can't be read;
          // keep the page locked (and polling) rather than unlocking it.
          response = { id: lockStatus.active_request_id, status: lockStatus.status || "in_progress" }
        }
      }
    }

    yield put(getCpaBulkEditStatusSuccess(normalizeBulkEditJob(response)))
  } catch (error) {
    yield put(getCpaBulkEditStatusError(error))
  }
}

function* AllProspectsSaga() {
  yield takeLatest(GET_ALL_PROSPECTS, onGetAllProspects)
  yield takeEvery(UPDATE_CPA_DISPOSITION, onUpdateCpaDisposition)
  yield takeEvery(BULK_EDIT_CPA_DISPOSITION, onBulkEditCpaDisposition)
  yield takeLatest(GET_CPA_BULK_EDIT_STATUS, onGetCpaBulkEditStatus)
  // takeLatest: if the user flips through dispositions quickly, only the
  // most recent preview request's response should ever land.
  yield takeLatest(PREVIEW_CPA_BULK_EDIT, onPreviewBulkEditCpaDisposition)
}

export default AllProspectsSaga
